import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { Row, Col, Card, CardHeader, CardBody, CardTitle, ButtonToolbar, ButtonGroup, Label, Input } from 'reactstrap';
import { formatBytes } from '../../utils';

const brandPrimary = '#20a8d8';
const brandSuccess = '#4dbd74';
const brandInfo = '#63c2de';
const brandWarning = '#f8cb00';
const brandDanger = '#f86c6b';

// convert Hex to RGBA
function convertHex(hex, opacity) {
  hex = hex.replace('#', '');
  var r = parseInt(hex.substring(0, 2), 16);
  var g = parseInt(hex.substring(2, 4), 16);
  var b = parseInt(hex.substring(4, 6), 16);

  var result = 'rgba(' + r + ',' + g + ',' + b + ',' + opacity / 100 + ')';
  return result;
}

function getWeekNumber(d) {
  // Copy date so don't modify original
  d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  // Get first day of year
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  var weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  // Return array of year and week number
  return [d.getUTCFullYear(), weekNo];
}

const StoreCdnUsage = ({ logs, unit, setUnit }) => {
  const [counter, setCounter] = useState(0);

  const refresh = () => {
    setCounter(counter + 1);
  };

  // Get data
  let groupData = [];
  if (logs) {
    for (let i = 0; i < logs.length; i++) {
      let group;
      const cdnUsageSize = logs[i].cdnUsageSize;
      const cdnUsageDate = new Date(logs[i].cdnUsageDate);
      const y = cdnUsageDate.getUTCFullYear();
      const m = 1 + cdnUsageDate.getUTCMonth();
      const d = cdnUsageDate.getUTCDate();
      if (unit === 'M') {
        // Month
        group = '' + y + '-' + (m < 10 ? `0${m}` : m);
      } else if (unit === 'W') {
        // Week
        const weekRes = getWeekNumber(cdnUsageDate);
        group = weekRes[0] + '-W' + weekRes[1];
      } else {
        // Day
        group = '' + y + '-' + (m < 10 ? `0${m}` : m) + '-' + (d < 10 ? `0${d}` : d);
      }

      const index = groupData.findIndex((element) => element.group == group);
      if (index === -1) {
        groupData.push({
          group,
          cdnUsageSize,
          order: parseInt(group.replace(/\D/g, '')),
          data: [logs[i]],
        });
      } else {
        groupData[index].cdnUsageSize += cdnUsageSize;
        groupData[index].data.push(logs[i]);
      }
    }
  }

  groupData.sort((a, b) => a.order - b.order);
  const n = unit === 'D' ? 30 : unit === 'W' ? 20 : 12;
  groupData = groupData.slice(-n);

  let groupMax = 0;
  const labels = [],
    data = [];
  for (let i = 0; i < groupData.length; i++) {
    labels.push(groupData[i].group);
    data.push(groupData[i].cdnUsageSize);
    groupMax = Math.max(groupMax, groupData[i].cdnUsageSize);
  }

  const mainChart = {
    labels,
    datasets: [
      {
        label: 'Display ' + (unit === 'D' ? 'Daily' : unit === 'W' ? 'Weekly' : 'Monthly') + ' data',
        backgroundColor: convertHex(brandInfo, 10),
        borderColor: brandInfo,
        pointHoverBackgroundColor: '#fff',
        borderWidth: 2,
        data,
      },
    ],
  };

  const mainChartOpts = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => formatBytes(ctx.parsed.y),
        },
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
        },
      },
      y: {
        beginAtZero: true,
        max: groupMax > 100 ? 100 * parseInt((groupMax * 1.2) / 100) : 100,
        ticks: {
          callback: (value) => formatBytes(value),
        },
      },
    },
    elements: {
      // chart.js 4 defaults lines to tension 0 (straight) + fill off; restore the
      // chart.js 2 smooth, filled-to-origin look used pre-upgrade / on staging.
      line: {
        tension: 0.4,
        fill: true,
      },
      point: {
        radius: 2,
        hitRadius: 10,
        hoverRadius: 4,
        hoverBorderWidth: 3,
      },
    },
  };

  return (
    <>
      <Row>
        <Col sm="5">
          <CardTitle className="mb-0" onClick={() => refresh()}>
            CDN Usage
          </CardTitle>
          <div className="small text-muted">The amount of CDN bandwidth data downloaded by users</div>
        </Col>
        <Col sm="7" className="d-none d-sm-inline-block">
          <ButtonToolbar className="float-end" aria-label="Toolbar with button groups">
            <ButtonGroup className="me-3" data-toggle="buttons" aria-label="First group">
              <Label htmlFor="option1" className={'btn btn-outline-secondary ' + (unit === 'D' ? 'active' : '')}>
                <Input type="radio" id="option1" checked={unit === 'D'} onClick={() => setUnit('D')} /> Day
              </Label>
              <Label htmlFor="option2" className={'btn btn-outline-secondary ' + (unit === 'W' ? 'active' : '')}>
                <Input type="radio" id="option2" checked={unit === 'W'} onClick={() => setUnit('W')} /> Week
              </Label>
              <Label htmlFor="option3" className={'btn btn-outline-secondary ' + (unit === 'M' ? 'active' : '')}>
                <Input type="radio" id="option3" checked={unit === 'M'} onClick={() => setUnit('M')} /> Month
              </Label>
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <div className="chart-wrapper" style={{ height: 300 + 'px', marginTop: 20 + 'px' }}>
        <Line data={mainChart} options={mainChartOpts} height={300} />
      </div>
    </>
  );
};

StoreCdnUsage.propTypes = {
  logs: PropTypes.any,
  unit: PropTypes.string,
  setUnit: PropTypes.func,
};

export default StoreCdnUsage;
